import React, {
	type ReactNode,
	useContext,
	useMemo,
	createContext,
	useState,
	useEffect,
	useCallback,
} from 'react';
import noop from 'lodash/noop';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ChoreographerContextProvider } from '@atlassian/jira-choreographer-services/src/ChoreographerContextProvider.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import messages from './messages.tsx';

/* TODO: remove after cleaning jira_list_hide_done_items and all the Spotlight targets  */
export const HIDE_DONE_ONBOARDING_ENGAGEMENT_ID_STG = 'jira-nin-hide-done-onboarding-stg';
export const HIDE_DONE_ONBOARDING_ENGAGEMENT_ID_PROD = 'jira-nin-hide-done-onboarding';

const ENV_STAGING: Environment = 'staging';
const STARGATE_URL = '/gateway/api';

/* Onboarding context to open the more menu from the issue-table */
type HideDoneOnboardingContextType = {
	showHideDoneOnboarding: boolean;
	setShowHideDoneOnboarding: (isMenuOpen: boolean) => void;
};

const HideDoneOnboardingContext = createContext<HideDoneOnboardingContextType | null>(null);

export const HideDoneOnboardingProvider = ({ children }: { children: ReactNode }) => {
	const [showHideDoneOnboarding, setShowHideDoneOnboarding] = useState(false);

	const value = useMemo(
		() => ({
			showHideDoneOnboarding,
			setShowHideDoneOnboarding,
		}),
		[showHideDoneOnboarding],
	);

	return (
		<HideDoneOnboardingContext.Provider value={value}>
			{children}
		</HideDoneOnboardingContext.Provider>
	);
};

export const useHideDoneOnboardingContext = () => {
	const hierarchyContext = useContext(HideDoneOnboardingContext);

	if (!hierarchyContext) {
		fireErrorAnalytics({
			meta: {
				id: 'issueTableHideDoneOnboarding',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error: new Error(
				'useHideDoneOnboardingContext must be used within a HideDoneOnboardingProvider',
			),
			sendToPrivacyUnsafeSplunk: true,
		});
		return {
			showHideDoneOnboarding: false,
			setShowHideDoneOnboarding: noop,
		};
	}

	return hierarchyContext;
};

export const useHideDoneOnboarding = ({
	isHideDoneSupportEnabled,
}: {
	isHideDoneSupportEnabled?: boolean;
}) => {
	const { showHideDoneOnboarding, setShowHideDoneOnboarding } = useHideDoneOnboardingContext();
	const ONBOARDING_ENGAGEMENT_ID =
		useEnvironment() === ENV_STAGING
			? HIDE_DONE_ONBOARDING_ENGAGEMENT_ID_STG
			: HIDE_DONE_ONBOARDING_ENGAGEMENT_ID_PROD;
	const cloudId = useCloudId();

	/*
	 * Onboarding Experience is loaded manually on page load via EP's CoordinationClient class
	 * Once loaded, if the message is active for this particular context, only then the HideDone_ONBOARDING will be loaded and displayed
	 */
	const coordinationClient = useMemo(
		() => new CoordinationClient(cloudId, STARGATE_URL, false, '', 'engagement'),
		[cloudId],
	);

	const [isEnrolled, stopEnrolment] = useCoordination(coordinationClient, ONBOARDING_ENGAGEMENT_ID);

	useEffect(() => {
		if (isEnrolled && isHideDoneSupportEnabled) {
			stopEnrolment(false);
			setShowHideDoneOnboarding(true);
		}
	}, [isEnrolled, isHideDoneSupportEnabled, setShowHideDoneOnboarding, stopEnrolment]);

	return {
		showHideDoneOnboarding,
		setShowHideDoneOnboarding,
	};
};

export const HideDoneOnboarding = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSpotlightActive, setIsSpotlightActive] = useState(true);
	const { showHideDoneOnboarding, setShowHideDoneOnboarding } = useHideDoneOnboardingContext();

	const onCloseSpotlight = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'hideDoneOnboardingButton');
		setIsSpotlightActive(false);
		setShowHideDoneOnboarding(false);
	}, [createAnalyticsEvent, setShowHideDoneOnboarding]);

	if (!showHideDoneOnboarding || !isSpotlightActive) {
		return;
	}

	return (
		<ChoreographerContextProvider isChoreographed>
			<JiraSpotlight
				messageId="issue-table-hide-done-toggle.ui.onboarding.jira-spotlight.nin-hide-done-toggle-spotlight"
				messageType="engagement"
				dialogPlacement="left top"
				actions={[
					{
						onClick: onCloseSpotlight,
						text: formatMessage(messages.ok),
					},
				]}
				dialogWidth={270}
				heading={formatMessage(messages.onboardingHeading)}
				target="nin-hide-done-toggle-spotlight"
				key="nin-hide-done-toggle-spotlight"
				targetRadius={3}
				targetBgColor={token('elevation.surface')}
			>
				{formatMessage(messages.onboardingDescription)}
			</JiraSpotlight>
		</ChoreographerContextProvider>
	);
};
